import { useEffect, useRef, useState } from "react";
import { useAuth } from "@lib/auth-client";
import { useReviewSubmission } from "@lib/hooks";
import type { ReviewSubmission } from "@lib/api-client";
import { fetchClientConfig } from "@lib/config";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (id?: string) => void;
    };
  }
}

interface ReviewFormProps {
  productId: string;
}

export function ReviewForm({ productId }: ReviewFormProps) {
  const { user, login } = useAuth();
  const mutation = useReviewSubmission(productId);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [siteKey, setSiteKey] = useState<string>("");
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    fetchClientConfig().then((config) => {
      setSiteKey(config.turnstileSiteKey);
    });
  }, []);

  useEffect(() => {
    if (!siteKey || !widgetRef.current) return;
    const scriptId = "cf-turnstile";
    const renderWidget = () => {
      if (!widgetRef.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(widgetRef.current, {
        sitekey: siteKey,
        callback: (token) => setTurnstileToken(token)
      });
    };
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    } else {
      renderWidget();
    }
  }, [siteKey]);

  if (!user) {
    return (
      <div className="review-form">
        <p>You need to be logged in to leave a review.</p>
        <button type="button" onClick={() => login()}>Log in with Google</button>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!turnstileToken) {
      alert("Please complete the Turnstile challenge.");
      return;
    }
    const payload: ReviewSubmission = {
      product_id: productId,
      rating,
      title,
      body,
      turnstileToken
    };
    try {
      await mutation.mutateAsync(payload);
      setTitle("");
      setBody("");
      setTurnstileToken("");
      if (widgetId.current && window.turnstile) {
        window.turnstile.reset(widgetId.current);
      }
      alert("Thanks for your review! We'll publish it after moderation if needed.");
    } catch (error) {
      console.error("Review submission failed", error);
    }
  };

  return (
    <form className="review-form" onSubmit={handleSubmit}>
      <h3>Share your experience</h3>
      <label>
        Rating
        <select value={rating} onChange={(event) => setRating(Number(event.target.value))}>
          {[1, 2, 3, 4, 5].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <label>
        Title
        <input value={title} onChange={(event) => setTitle(event.target.value)} required minLength={3} />
      </label>
      <label>
        Review
        <textarea value={body} onChange={(event) => setBody(event.target.value)} required minLength={40} />
      </label>
      <div ref={widgetRef} className="turnstile-widget" />
      <button type="submit" disabled={mutation.isLoading}>
        {mutation.isLoading ? "Submitting…" : "Submit review"}
      </button>
      {mutation.isError ? <p className="error">{(mutation.error as Error)?.message ?? "Submission failed"}</p> : null}
    </form>
  );
}
